import { CURRENT_SCHEMA_VERSION, type ProjectDocument } from './types';
import { parseProjectDocument, type ValidationIssue } from './validate';

/**
 * Project schema migrations (docs/09 "Versioning and migrations"). Each migration is
 * a pure function from one validated schema version to the next, tested with before and
 * after fixtures. Format 1 was the first published format; format 2 adds filled shapes.
 */
export interface Migration {
  readonly from: number;
  readonly to: number;
  readonly description: string;
  /** Must not mutate its input. May report notes about appearance-affecting changes. */
  migrate(input: Record<string, unknown>, notes: string[]): Record<string, unknown>;
}

export const MIGRATIONS: readonly Migration[] = [
  {
    from: 1,
    to: 2,
    description: 'Filled shapes (ADR 0016)',
    // Format 2 only adds possibilities (fills, paths without a line), so every format 1
    // project is already a valid format 2 project: nothing about its art changes.
    migrate: (input) => ({ ...input, schemaVersion: 2 }),
  },
];

export type MigrationOutcome =
  | {
      readonly ok: true;
      readonly doc: ProjectDocument;
      readonly fromVersion: number;
      readonly migrated: boolean;
      readonly notes: readonly string[];
    }
  | {
      readonly ok: false;
      readonly reason: 'future-version' | 'unknown-version' | 'invalid' | 'not-an-object';
      readonly message: string;
      readonly issues?: readonly ValidationIssue[];
    };

export function migrateProject(
  input: unknown,
  migrations: readonly Migration[] = MIGRATIONS,
  currentVersion: number = CURRENT_SCHEMA_VERSION,
): MigrationOutcome {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return {
      ok: false,
      reason: 'not-an-object',
      message: 'The file does not contain a Jamie’s Nails Pro project.',
    };
  }
  const raw = input as Record<string, unknown>;
  const version = raw.schemaVersion;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 0) {
    return {
      ok: false,
      reason: 'unknown-version',
      message: 'The project has no recognizable schema version.',
    };
  }
  if (version > currentVersion) {
    return {
      ok: false,
      reason: 'future-version',
      message: `This project was saved by a newer version of Jamie’s Nails Pro (format ${version}). Update the app to open it. The file has not been changed.`,
    };
  }
  let current: Record<string, unknown> = raw;
  let at = version;
  const notes: string[] = [];
  while (at < currentVersion) {
    const step = migrations.find((m) => m.from === at);
    if (!step) {
      return {
        ok: false,
        reason: 'unknown-version',
        message: `No upgrade path exists from project format ${at}.`,
      };
    }
    current = step.migrate(current, notes);
    if (current.schemaVersion !== step.to) {
      return {
        ok: false,
        reason: 'invalid',
        message: `Upgrade step ${step.from}→${step.to} produced the wrong version.`,
      };
    }
    at = step.to;
  }
  const parsed = parseProjectDocument(current);
  if (!parsed.ok) {
    return {
      ok: false,
      reason: 'invalid',
      message: 'The project data is not valid.',
      issues: parsed.issues,
    };
  }
  return {
    ok: true,
    doc: parsed.value,
    fromVersion: version,
    migrated: version !== currentVersion,
    notes,
  };
}
